import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

const offlineMessages = [
  "It looks like your internet's taking a break!",
  "Still no signal... time to reconnect?",
  "The connection must be on vacation.",
  "Lost in cyberspace? Check your connection.",
  "Your internet seems to have vanished.",
  "Let’s get you back online soon.",
  "Hmm, is the Wi-Fi hiding?",
  "Signal down... the search for connection continues.",
  "No connection detected. Please try again.",
  "Is your Wi-Fi feeling shy today?",
  "Still offline... time to troubleshoot.",
  "Let’s bring the internet back to life!",
  "Are you too far from the router?",
  "Weak signal, stronger will to reconnect.",
  "Maybe the router needs a hug?",
  "Give it a moment, the signal will return.",
  "Checking your cables might help.",
  "Make sure airplane mode isn't on!",
  "Network issues: Try restarting your router.",
  "Your internet is in stealth mode.",
  "Reconnect and refresh when ready.",
  "Let’s fix that connection issue.",
  "You’re offline... but not for long!",
  "Trouble connecting? We’re working on it.",
  "Is your network taking a nap?",
  "It might be time for a router reboot.",
  "No internet... but we’ll fix it!",
  "No connection found. Try checking your settings.",
  "Your signal's lost in cyberspace.",
  "Hmm, still no connection detected.",
  "Double-check your cables and settings.",
  "Connection failure... time to troubleshoot.",
  "No Wi-Fi in sight. Let's fix that!",
  "Stay calm, we’ll get you back online soon.",
  "Looks like the signal is playing hide-and-seek.",
  "Reconnect and give it another go.",
  "The internet gods are not smiling upon us.",
  "Your signal has taken a short break.",
  "Try reconnecting to the network manually.",
  "Still no connection... have you tried rebooting?",
  "Check your Wi-Fi router for clues.",
  "It’s been offline for a bit... time for action!",
  "No internet, but don’t lose hope!",
  "Connection not found, try again soon.",
  "We're still searching for the signal.",
  "Trouble finding the network? Let’s troubleshoot.",
  "It’s offline, but not forever.",
  "Weak signal detected... might be time to restart.",
  "Network unavailable. Check your router.",
  "Signal outage detected. Hold tight!",
  "Connection lost, but it’s coming back.",
  "Looks like you're disconnected. Try again.",
  "No signal in sight... try again soon.",
  "The Wi-Fi's having a rough day.",
  "Connection failure. Time for a router reset?",
  "Still no signal... troubleshooting is key.",
  "Your connection's gone into hiding.",
  "No Wi-Fi detected. Maybe it's shy?",
  "The internet seems to be down.",
  "Hmm, we’re offline for now.",
  "Try reconnecting your network.",
  "Wi-Fi signal seems to have vanished.",
  "Your connection took a short break.",
  "Looks like we're still disconnected.",
  "The network has gone offline.",
  "Signal is lost, try refreshing.",
  "Connection missing... troubleshoot?",
  "No signal detected, try rebooting.",
  "The router might need a refresh.",
  "It’s offline... let's get back online!",
  "The internet seems to have left the building.",
  "Hmm, where’s the Wi-Fi today?",
  "We’re having trouble reconnecting.",
  "Still disconnected... troubleshoot time?",
  "The network is unavailable right now.",
  "Your Wi-Fi is out, but not forever.",
  "Lost signal... but we’ll find it soon.",
  "It’s offline, time to check settings.",
  "Network lost... try rebooting?",
  "We’re still working on reconnecting.",
  "Network error detected. Try again.",
  "Still disconnected... troubleshoot away!",
  "The signal’s taken a break.",
  "It seems like you’ve gone offline.",
  "Your connection is temporarily unavailable.",
  "No internet detected, check your settings.",
  "Your Wi-Fi might need a reset.",
  "Still offline... don’t worry, we’ll fix it.",
  "The internet seems to have taken a break.",
  "No signal detected. Have you tried resetting?",
  "Network issues detected, let's reconnect.",
  "It looks like we’ve lost your connection.",
  "Connection lost, try refreshing.",
  "No Wi-Fi detected, time for a router check?",
  "Your signal is missing. Let’s fix that.",
  "No connection available right now.",
  "The internet has gone offline for now.",
];

const NetworkStatus = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [message, setMessage] = useState("");

  const animationVariants = {
    initial: { opacity: 0, y: -20 },
    animate: {
      opacity: 1,
      y: 0,
      rotate: [0, 10, -10, 0],
      transition: { duration: 1.5, repeat: Infinity, repeatType: "mirror" },
    },
  };

  useEffect(() => {
    const updateNetworkStatus = () => {
      setIsOffline(!navigator.onLine);
    };

    window.addEventListener("online", updateNetworkStatus);
    window.addEventListener("offline", updateNetworkStatus);

    // Set initial message
    if (isOffline) {
      setMessage(
        offlineMessages[Math.floor(Math.random() * offlineMessages.length)]
      );
    }

    return () => {
      window.removeEventListener("online", updateNetworkStatus);
      window.removeEventListener("offline", updateNetworkStatus);
    };
  }, [isOffline]);

  useEffect(() => {
    let interval;
    if (isOffline) {
      interval = setInterval(() => {
        setMessage(
          offlineMessages[Math.floor(Math.random() * offlineMessages.length)]
        );
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isOffline]);

  return (
    <div className="flex justify-center items-center w-screen h-screen bg-gray-100">
      <motion.div
        className="bg-white shadow-lg rounded-lg p-6 w-[90vw] h-[50vh] sm:w-[80vw] sm:h-[50vh] md:w-[60vw] lg:w-[40vw] xl:w-[30vw] xl:h-[48vh] 2xl:w-[25vw] mx-auto flex flex-col justify-center items-center gap-5"
        initial={{ scale: 0.9 }}
        animate={{ scale: 1, transition: { duration: 0.5 } }}
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="150"
          height="150"
          variants={animationVariants}
          initial="initial"
          animate="animate"
        >
          <path
            fill="#FF6347"
            d="M12 18c-1.104 0-2 .896-2 2h4c0-1.104-.896-2-2-2zm4.243-2.757l1.414-1.414C15.522 12.436 13.824 12 12 12c-1.824 0-3.522.436-5.657 1.829l1.414 1.414C9.522 14.098 10.708 14 12 14c1.292 0 2.478.098 3.657.829zM18.364 9.636c-2.633-2.134-6.095-2.134-8.728 0l1.414 1.414c2.072-1.732 4.828-1.732 6.899 0l1.414-1.414zm2.121-2.122C15.402 3.144 8.598 3.144 3.515 7.515l1.414 1.414C8.33 6.267 11.67 6.267 15.07 8.93l1.415-1.415zm-7.07 7.07l-4.243 4.243 1.414 1.414 4.243-4.243-1.414-1.414z"
          />
        </motion.svg>

        <motion.h2
          className="text-lg sm:text-2xl text-center font-semibold mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { delay: 0.3, duration: 0.8 } }}
        >
          Connection Lost
        </motion.h2>
        <motion.p
          className="text-gray-600 text-center text-sm sm:text-base"
          key={message}
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" },
          }}
          exit={{ opacity: 0, y: 20 }}
        >
          {message}
        </motion.p>
      </motion.div>
    </div>
  );
};

export default NetworkStatus;
